import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface LanguageSelectorProps {
  scrolled?: boolean;
}

export default function LanguageSelector({ scrolled = false }: LanguageSelectorProps) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === "zh-Hant" || i18n.language === "zh" ? "zh-Hant" : "en";

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const switchTo = (lang: string) => {
    i18n.changeLanguage(lang);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        aria-label={t("language.label")}
        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium tracking-wider whitespace-nowrap cursor-pointer transition-colors ${
          scrolled
            ? "text-foreground-700 hover:bg-background-100"
            : "text-background-50/90 hover:bg-background-50/10"
        }`}
      >
        <i className="ri-global-line w-4 h-4 flex items-center justify-center"></i>
        <span className="hidden sm:inline">{currentLang === "zh-Hant" ? "繁中" : "EN"}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 min-w-[140px] bg-background-50 border border-background-200 rounded-md py-1 shadow-[0_4px_16px_rgba(0,0,0,0.08)] z-50">
          <button
            onClick={() => switchTo("en")}
            className={`w-full text-left px-4 py-2 text-sm cursor-pointer whitespace-nowrap transition-colors ${
              currentLang === "en"
                ? "bg-primary-50 text-primary-700 font-medium"
                : "text-foreground-800 hover:bg-background-100"
            }`}
          >
            {t("language.en")}
          </button>
          <button
            onClick={() => switchTo("zh-Hant")}
            className={`w-full text-left px-4 py-2 text-sm cursor-pointer whitespace-nowrap transition-colors ${
              currentLang === "zh-Hant"
                ? "bg-primary-50 text-primary-700 font-medium"
                : "text-foreground-800 hover:bg-background-100"
            }`}
          >
            {t("language.zh")}
          </button>
        </div>
      )}
    </div>
  );
}